import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import SchoolForm from './SchoolForm';
import SchoolsFiltersAndSearch from './SchoolsFiltersAndSearch';
import { SCHOOLS_TABLE_COLUMN_DEFINITION } from './SchoolsList.constants';
import { loadSchoolsAction } from '../schools.actions';
import { selectSchoolsPageConfiguration, selectSchoolsTableData } from '../schools.selectors';
import { clearCurrentSchoolAction, loadSchoolAction } from '../../SchoolProfile/SchoolDetails';
import Button, { ButtonIcons, ButtonSizes } from '../../../../components/Button';
import Modal, { MODAL_ANIMATION_DURATION } from '../../../../components/Modal';
import Pager, { INITIAL_PAGE } from '../../../../components/Pager';
import Table, { TableHeaderColumnTypes } from '../../../../components/Table';
import Typography, { TypographyVariants } from '../../../../components/Typography';
import { ASCENDING_ORDER_DIRECTION, DESCENDING_ORDER_DIRECTION } from '../../../../shared/constants';
import { loadResourcesAction, selectResources } from '../../../../shared/state/resources';
import { loadStatusesAction, selectStatuses } from '../../../../shared/state/statuses';

const SchoolsList: FunctionComponent = () => {
  const dispatch = useDispatch();
  const pageConfig = useSelector(selectSchoolsPageConfiguration);
  const schoolsData = useSelector(selectSchoolsTableData);

  const accreditations = useSelector(selectResources('accreditations'));
  const schoolStatuses = useSelector(selectStatuses('school'));

  const [isSchoolEdit, setIsSchoolEdit] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(loadSchoolsAction());
    dispatch(loadResourcesAction('accreditations'));
    dispatch(loadStatusesAction('school'));
  }, [dispatch]);

  useEffect(() => {
    if (isModalOpen) {
      closeModal();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolsData]);

  const handlePageChanged = (page: number): void => {
    dispatch(loadSchoolsAction({ ...pageConfig, page }));
  };

  const handleSortByChanged = (fieldName: string): void => {
    const orderDirection =
      pageConfig.sortBy !== fieldName
        ? DESCENDING_ORDER_DIRECTION
        : pageConfig.order === ASCENDING_ORDER_DIRECTION
        ? DESCENDING_ORDER_DIRECTION
        : ASCENDING_ORDER_DIRECTION;
    dispatch(loadSchoolsAction({ ...pageConfig, sortBy: fieldName, order: orderDirection }));
  };

  const openPopup = (id?: number): void => {
    if (id) {
      dispatch(loadSchoolAction(id));
      setIsSchoolEdit(true);
    } else {
      setIsSchoolEdit(false);
    }

    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setTimeout(() => {
      dispatch(clearCurrentSchoolAction());
    }, MODAL_ANIMATION_DURATION);
  };

  const schoolsTableConfig = [
    ...SCHOOLS_TABLE_COLUMN_DEFINITION,
    {
      id: 'actions',
      title: 'Action',
      type: TableHeaderColumnTypes.actions,
      width: '90px', // TODO change it to 150px when delete action is added.
      actions: [
        {
          label: 'Edit',
          handleOnClick: openPopup,
        },
      ],
    },
  ];

  return (
    <>
      <div className="mba-heading--wrapper">
        <Typography component="p" variant={TypographyVariants.h1} className="mba-heading--title">
          Business schools
        </Typography>
        <Button
          text="Create new school"
          size={ButtonSizes.big}
          icon={ButtonIcons.plus}
          disabled={isModalOpen}
          primary
          onClick={(): void => openPopup()}
        />
      </div>
      <div className="mba-table-wrapper">
        <div className="mba-table-filters">
          <SchoolsFiltersAndSearch pageConfig={pageConfig} filtersData={{ schoolStatuses, accreditations }} />
        </div>
        <div className="mba-table-container">
          <Table
            id="schools-list"
            columns={schoolsTableConfig}
            data={schoolsData}
            sortingOptions={{
              order: pageConfig.order || DESCENDING_ORDER_DIRECTION,
              sortBy: pageConfig.sortBy || '',
            }}
            onSortByColumn={handleSortByChanged}
          />
        </div>
      </div>
      {pageConfig && pageConfig.totalPages !== INITIAL_PAGE && (
        <Pager
          currentPage={pageConfig.page}
          totalPages={pageConfig.totalPages || INITIAL_PAGE}
          onPageChange={handlePageChanged}
        />
      )}
      {isModalOpen && (
        <Modal
          id="school-modal"
          title={`${isSchoolEdit ? 'Edit' : 'Create new'} school`}
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <SchoolForm onFormClose={closeModal} isEdit={isSchoolEdit} isOpen={isModalOpen} />
        </Modal>
      )}
    </>
  );
};

export default SchoolsList;
